import { useRouter } from "next/router";
import { FC, createContext, useContext, useEffect, useRef, useState } from "react";

interface NavigationContextType {
    previousUrl?: string;
    historyStack: string[];
}

const NavigationContext = createContext<NavigationContextType>({
    previousUrl: undefined,
    historyStack: [],
});

const MAX_HISTORY_SIZE = 50; // Adjust as needed

/**
 * Provider component for managing navigation state.
 *
 * @component
 */
export const NavigationProvider: FC = ({ children }) => {
    const [previousUrl, setPreviousUrl] = useState<string | undefined>();
    const [historyStack, setHistoryStack] = useState<string[]>([]);
    const router = useRouter();

    const initialized = useRef(false);

    // Initialize history with current route
    useEffect(() => {
        if (router.isReady && router.asPath && !initialized.current) {
            setHistoryStack([router.asPath]);
            initialized.current = true;
        }
    }, [router.isReady]);

    // Handles route changes by updating the previous URL and history stack.
    const handleRouteChange = (url: string) => {
        setHistoryStack(prevStack => {
            const newStack = [...prevStack];

            // Don't modify stack if it's the same URL
            if (newStack[newStack.length - 1] === url) {
                return newStack;
            }

            // Store the previous URL before modifying stack
            const previousUrl = newStack[newStack.length - 1];
            setPreviousUrl(previousUrl);

            // Handle back navigation
            if (newStack.includes(url)) {
                const index = newStack.indexOf(url);
                // If it's a back navigation, trim the stack
                return newStack.slice(0, index + 1);
            }

            // Add new route
            newStack.push(url);

            return newStack.slice(-MAX_HISTORY_SIZE);
        });
    };

    useEffect(() => {
        router.events.on("routeChangeComplete", handleRouteChange);
        return () => {
            router.events.off("routeChangeComplete", handleRouteChange);
        };
    }, [router, handleRouteChange]);

    return <NavigationContext.Provider value={{ previousUrl, historyStack }}>{children}</NavigationContext.Provider>;
};

/**
 * Hook to access the navigation context.
 *
 * @returns {Object} An object containing the previous URL and history stack.
 * @throws {Error} If used outside of NavigationProvider.
 */
export const useNavigation = () => {
    const context = useContext(NavigationContext);
    if (!context) {
        throw new Error("useNavigation must be used within a NavigationProvider");
    }
    return context;
};
